import { FULL_PORTAL_ROUTES, PORTAL_ROUTE_GROUPS } from "../packageAccessStatic";

/**
 * Maps webPortalAccess module keys → route keys (not used to grant access by tier).
 */
export const WEB_PORTAL_MODULE_ROUTES = Object.freeze({
  dashboard: ["DASHBOARD"],
  profile: ["PROFILE"],
  admin: ["ADMIN", "CREATE_ADMIN", "MANAGE_ADMIN"],
  clients: ["CLIENTS", "CREATE_CLIENT", "MANAGE_CLIENT", "CLIENT_SUMMARY"],
  employees: [
    "EMPLOYEE",
    "CREATE_EMPLOYEE",
    "MANAGE_EMPLOYEE",
    "MANAGE_GROUPS",
    "INACTIVE_EMPLOYEE",
  ],
  invoices: ["INVOICE", "CREATE_INVOICE", "MANAGE_INVOICE", "CRT_INV", "MNG_INV"],
  scheduling: [...PORTAL_ROUTE_GROUPS.scheduling],
  proof_of_service: ["PROOF_OF_SERVICE", "POS_WORK"],
  issues: [...PORTAL_ROUTE_GROUPS.issues],
  supplies: [...PORTAL_ROUTE_GROUPS.supplies],
  notifications: ["NOTIFICATION", "CREATE_NOTIFICATION", "MANAGE_NOTIFICATION"],
  client_notifications: [
    "NOTIFICATION",
    "CREATE_NOTIFICATION",
    "MANAGE_NOTIFICATION",
  ],
  alerts: [...PORTAL_ROUTE_GROUPS.alerts],
  messaging: [...PORTAL_ROUTE_GROUPS.teamChat],
  reports_standard: [
    ...PORTAL_ROUTE_GROUPS.reports.filter(
      (r) => !PORTAL_ROUTE_GROUPS.advancedReports.includes(r)
    ),
  ],
  reports_advanced: [...PORTAL_ROUTE_GROUPS.advancedReports],
  client_portal: ["CLIENT_PORTAL"],
  sms: ["SMS_NOTIFICATION"],
  push: ["PUSH"],
  settings: [
    "SETTINGS",
    "APPOINTMENT_SETTINGS",
    "NOTIFICATION_SETTINGS",
    "ISSUES_SETTINGS",
    "CLIENT_SETTINGS",
    "COMPANY_SUBSCRIPTION",
    "SUPPLY_SETTINGS",
  ],
  billing: ["BILLING", "UPGRADE"],
  support: [
    "SUPPORT",
    "CREATE_SUPPORT",
    "SUPPORT_DETAILS",
    "TECHNICAL_SUPPORT",
    "MANAGE_SUPPORT",
    "ARCHIVE_SUPPORT",
    "ARCHIVE_SUPPORT_DETAILS",
    "CONTACT_US",
  ],
  accounts: ["ACCOUNTS"],
});

/** Modules that default on unless explicitly false in webPortalAccess. */
const ALWAYS_ENABLED_MODULES = Object.freeze([
  "dashboard",
  "profile",
  "admin",
  "clients",
  "employees",
  "invoices",
  "settings",
  "billing",
  "support",
  "accounts",
]);

const OPTIONAL_MODULES = Object.freeze([
  "scheduling",
  "proof_of_service",
  "issues",
  "supplies",
  "notifications",
  "alerts",
  "messaging",
  "reports_standard",
  "reports_advanced",
  "client_portal",
  "client_notifications",
  "sms",
  "push",
]);

/**
 * Build portal route keys from a normalized plansAccess doc (webPortalAccess only).
 * Does not read static tier route lists.
 * @param {{ webPortalAccess?: Record<string, boolean> }} matchedPlan
 * @returns {string[]}
 */
export function buildRoutesFromNormalizedPlan(matchedPlan) {
  const wpa = matchedPlan?.webPortalAccess || {};
  const routeSet = new Set();

  if (wpa.fullAccess === true) {
    FULL_PORTAL_ROUTES.forEach((routeKey) => routeSet.add(routeKey));
    Object.entries(WEB_PORTAL_MODULE_ROUTES).forEach(([moduleKey, routeKeys]) => {
      if (wpa[moduleKey] === false) {
        routeKeys.forEach((routeKey) => routeSet.delete(routeKey));
      }
    });
    return Array.from(routeSet);
  }

  ALWAYS_ENABLED_MODULES.forEach((moduleKey) => {
    if (wpa[moduleKey] === false) return;
    (WEB_PORTAL_MODULE_ROUTES[moduleKey] || []).forEach((routeKey) =>
      routeSet.add(routeKey)
    );
  });

  OPTIONAL_MODULES.forEach((moduleKey) => {
    if (wpa[moduleKey] !== true) return;
    (WEB_PORTAL_MODULE_ROUTES[moduleKey] || []).forEach((routeKey) =>
      routeSet.add(routeKey)
    );
  });

  return Array.from(routeSet);
}
